<template>
  <v-container fluid>
    <div class="toolbar d-flex flex-row justify-space-between align-center elevation-3 mb-3">
      <span>
        จัดการ Distributor
      </span>
      <div
        class="d-flex flex-row align-center"
        style="gap: 0.625rem;">
        <v-btn
          v-if="isEdit && isPasswordReset"
          color="error"
          outlined
          rounded
          small
          :loading="loading"
          @click="togglePasswordReset()">
          {{ $t('button.Back') }}
        </v-btn>
        <v-btn
          v-else-if="isEdit"
          color="error"
          outlined
          rounded
          small
          :loading="loading"
          @click="togglePasswordReset()">
          {{ $t('button.PasswordReset') }}
        </v-btn>
        <v-btn
          color="success"
          rounded
          small
          :loading="loading"
          @click="onSubmit()">
          {{ $t('button.Save') }}
        </v-btn>
      </div>
    </div>

    <v-form
      ref="distributor-form"
      @submit.prevent="onSubmit()">
      <v-row class="px-8">
        <v-col
          cols="12"
          class="card-container elevation-3">
          <v-row>
            <v-col
              cols="12"
              class="card-title">
              {{ isPasswordReset ? 'เปลี่ยนรหัสผ่าน' : 'ข้อมูลผู้ขาย' }} {{ isEdit && !loading ? `- "${formData.username}"` : '' }}
            </v-col>
            <v-col cols="12">
              <v-row>
                <template v-if="isEdit && isPasswordReset">
                  <v-col
                    cols="12"
                    md="3">
                    <v-text-field
                      v-model="formData.password"
                      :type="showPassword ? 'text' : 'password'"
                      label="รหัสผ่าน"
                      color="secondary"
                      :rules="passwordRules"
                      :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      dense
                      outlined
                      :disabled="loading"
                      @click:append="showPassword = !showPassword" />
                  </v-col>
                  <v-col
                    cols="12"
                    md="3">
                    <v-text-field
                      v-model="formData.confirmPassword"
                      :type="showPassword ? 'text' : 'password'"
                      label="ยืนยันรหัสผ่าน"
                      color="secondary"
                      :rules="confirmPasswordRules"
                      :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      dense
                      outlined
                      :disabled="loading"
                      @click:append="showPassword = !showPassword" />
                  </v-col>
                </template>
                <template v-else-if="!isEdit">
                  <v-col
                    cols="12"
                    md="3">
                    <v-text-field
                      v-model.trim="formData.username"
                      label="ชื่อผู้ใช้งาน"
                      color="secondary"
                      :rules="[ ...textBoxRules, ...whiteSpaceRule, maxLengthRule(100) ]"
                      dense
                      outlined
                      :disabled="loading" />
                  </v-col>
                  <v-col
                    cols="12"
                    md="3">
                    <v-text-field
                      v-model="formData.password"
                      type="password"
                      label="รหัสผ่าน"
                      color="secondary"
                      :rules="passwordRules"
                      dense
                      outlined
                      :disabled="loading" />
                  </v-col>
                </template>
                <template v-if="!isPasswordReset">
                  <v-col
                    cols="12"
                    md="3">
                    <v-text-field
                      v-model="formData.firstName"
                      label="ชื่อ"
                      color="secondary"
                      :rules="[ ...textBoxRules, ...whiteSpaceRule, maxLengthRule(100) ]"
                      dense
                      outlined
                      :disabled="loading" />
                  </v-col>
                  <v-col
                    cols="12"
                    md="3">
                    <v-text-field
                      v-model="formData.lastName"
                      label="นามสกุล"
                      :rules="[ ...textBoxRules, ...whiteSpaceRule, maxLengthRule(50) ]"
                      color="secondary"
                      dense
                      outlined
                      :disabled="loading" />
                  </v-col>
                  <v-col
                    cols="12"
                    md="3">
                    <v-autocomplete
                      v-model="formData.country"
                      :rules="textBoxRules"
                      label="ประเทศ"
                      color="secondary"
                      :items="shippingCountries"
                      item-text="country"
                      item-value="code"
                      dense
                      outlined
                      :disabled="loading" />
                  </v-col>
                  <v-col
                    cols="12"
                    md="3">
                    <v-autocomplete
                      v-model="formData.brand"
                      :rules="textBoxRules"
                      label="แบรนด์"
                      color="secondary"
                      :items="brands"
                      dense
                      outlined
                      :disabled="loading" />
                  </v-col>
                  <v-col
                    cols="12"
                    md="3">
                    <v-text-field
                      v-model="formData.tel"
                      :rules="[ ...numberRules, ...whiteSpaceRule, maxLengthRule(30) ]"
                      label="เบอร์โทร"
                      color="secondary"
                      dense
                      outlined
                      :disabled="loading" />
                  </v-col>
                  <v-col
                    cols="12"
                    md="3">
                    <v-text-field
                      v-model="formData.email"
                      :rules="[
                        ...whiteSpaceRule,
                        maxLengthRule(80),
                        (v) => !v || /^\S+@\S+\.\S+$/.test(v) || 'โปรดกรอกอีเมลให้ถูกต้อง'
                      ]"
                      label="อีเมล"
                      color="secondary"
                      dense
                      outlined
                      :disabled="loading" />
                  </v-col>
                </template>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import ShippingCountries from '@/assets/json/ShippingCountries.json'
import DistributorsProvider from '@/resources/DistributorsProvider'
import ProductAttributeProvider from '@/resources/ProductAttributeProvider'
import ExportCurrencyRatesProvider from '@/resources/ExportCurrencyRatesProvider'

const DistributorsService = new DistributorsProvider()
const ProductAttributeService = new ProductAttributeProvider()
const ExportCurrencyRatesService = new ExportCurrencyRatesProvider()

export default {
  data () {
    return {
      loading: true,
      isEdit: false,
      isPasswordReset: false,
      showPassword: false,
      textBoxRules: [
        (v) => !!v || 'โปรดกรอก Field นี้'
      ],
      whiteSpaceRule: [
        (v) => !v?.toString()?.includes(' ') || 'ห้ามมีช่องว่างใน Field นี้'
      ],
      numberRules: [
        (v) => (!Number.isNaN(parseFloat(v)) && Number.isFinite(Number(v))) || 'โปรดกรอกเฉพาะตัวเลข'
      ],
      shippingCountries: [],
      brands: [],
      formData: {
        username: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        tel: '',
        email: '',
        addresses: [],
        country: '',
        brand: ''
      }
    }
  },
  computed: {
    passwordRules () {
      return [...this.textBoxRules, ...this.whiteSpaceRule, this.maxLengthRule(100)]
    },
    confirmPasswordRules () {
      return [
        ...this.passwordRules,
        (v) => v === this.formData.password || 'รหัสผ่านไม่ตรงกัน'
      ]
    }
  },
  mounted () {
    if (this.$route.params?.id) {
      this.isEdit = true
    }

    this.initialize()
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),
    maxLengthRule (length) {
      return (v) => v?.length <= length || `Field นี้ต้องยาวไม่เกิน ${length} ตัวอักษร`
    },
    getShippingCountry (code) {
      return ShippingCountries.find((country) => country.code === code)?.country || code
    },
    async initialize () {
      try {
        this.loading = true

        await this.getBrands()
        await this.getCountries()

        if (this.isEdit) {
          await this.getItem()
        }
      } catch (error) {
        console.error('initialize', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async getBrands () {
      try {
        this.loading = true

        const { data } = await ProductAttributeService.getProductAttribute('brands', {
          limit: 9999,
          sortBy: 'createdAt',
          sortOrder: 'asc'
        })

        this.brands = [...data.results.map((re) => re.name)]
      } catch (error) {
        console.error('getBrands', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async getCountries () {
      try {
        this.loading = true

        const { data } = await ExportCurrencyRatesService.getItems({})

        this.shippingCountries = data.map((r) => ({
          country: this.getShippingCountry(r.country),
          code: r.country
        }))
      } catch (error) {
        console.error('getCountries', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async getItem () {
      try {
        this.loading = true

        const { data } = await DistributorsService.getItemById(this.$route.params.id)

        this.formData = {
          ...data,
          password: ''
        }
      } catch (error) {
        console.error('getItem', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    togglePasswordReset () {
      this.isPasswordReset = !this.isPasswordReset
      this.formData.password = ''
    },
    async onSubmit () {
      const validated = await this.$refs['distributor-form'].validate()

      if (!validated) {
        return
      }

      try {
        this.loading = true

        const payload = { ...this.formData }

        if (payload.password === '') {
          delete payload.password
        }

        if (this.isEdit) {
          await DistributorsService.updateItem(payload)
          this.setSnackbar({
            value: true,
            message: 'แก้ไข Distributor สำเร็จ',
            type: 'success'
          })
        } else {
          await DistributorsService.createItem(payload)
          this.setSnackbar({
            value: true,
            message: 'สร้าง Distributor สำเร็จ',
            type: 'success'
          })
        }

        this.$router.push({ name: 'ExportDistributors' })
      } catch (error) {
        console.error('onSubmit', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.toolbar {
  position: sticky;
  top: 70px;
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.toolbar span {
  font-weight: bold;
  font-size: 1.25rem;
}
.card-container {
  padding: 1.25rem 1.875rem;
  background-color: #fff;
  border-radius: 0.625rem;
  margin-top: 1.25rem;
}
.card-title {
  font-weight: bold;
  font-size: 1.35rem;
}
</style>

<style>
.switch-container .v-input--selection-controls {
  margin-top: 5px !important;
}
</style>
